import { useEffect, useRef, useState } from 'react';
import {
  VegaButton,
  VegaCard,
  VegaFlex,
  VegaFont,
  VegaInput,
  VegaPageNotification,
  VegaTable,
} from '@globalpayments/vega-react';
import type { VegaTableColumnConfig, VegaTableDataSourceItem } from '@globalpayments/vega/dist/types/components/vega-table/interface';

import PageSkeleton from '../../components/pageSkeleton';
import useDeleteUser from '../../hooks/useDeleteUser';
import useInviteUser from '../../hooks/useInviteUser';
import useResendLoginLink from '../../hooks/useResendLoginLink';
import useUpdateUserActive from '../../hooks/useUpdateUserActive';
import useUpdateUserRoles from '../../hooks/useUpdateUserRoles';
import useUsers from '../../hooks/useUsers';
import { UserRole, UserSummary } from '../../types/users';
import { useAuth } from '../../utility/auth';
import EditRolesDialog from './components/EditRolesDialog';
import DeleteUserDialog from './components/DeleteUserDialog';
import InviteUserDialog from './components/InviteUserDialog';
import ResendLoginLinkDialog from './components/ResendLoginLinkDialog';
import UpdateActiveDialog from './components/UpdateActiveDialog';
import styles from './index.module.css';

interface UserTableRecord extends VegaTableDataSourceItem {
  id: number;
  displayName: string;
  email: string;
  provider: string;
  status: string;
  roles: string;
  user: UserSummary;
}

const toTableRecord = (user: UserSummary): UserTableRecord => ({
  id: user.id,
  displayName: user.displayName,
  email: user.email,
  provider: user.provider,
  status: user.isActive ? 'Active' : 'Inactive',
  roles: user.roles.join(', '),
  user,
});

/** Displays the application user directory for administrators. */
const UserManagement = () => {
  const auth = useAuth();
  const canManageUsers = auth.hasRole(UserRole.Admin);
  const { data, isError, isLoading } = useUsers(canManageUsers);
  const inviteMutation = useInviteUser();
  const resendMutation = useResendLoginLink();
  const rolesMutation = useUpdateUserRoles();
  const activeMutation = useUpdateUserActive();
  const deleteMutation = useDeleteUser();
  const [selectedUser, setSelectedUser] = useState<UserSummary>();
  const [searchValue, setSearchValue] = useState('');
  const [dialog, setDialog] = useState<'invite' | 'resend' | 'roles' | 'active' | 'delete'>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const successTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(successTimer.current), []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    window.clearTimeout(successTimer.current);
    successTimer.current = window.setTimeout(() => setSuccessMessage(undefined), 3000);
  };

  const openUserDialog = (nextDialog: 'resend' | 'roles' | 'active' | 'delete', user: UserSummary) => {
    setSelectedUser(user);
    setDialog(nextDialog);
  };
  const columns: VegaTableColumnConfig[] = [
    { key: 'displayName', label: 'Name', prop: 'displayName', width: '200px' },
    {
      key: 'email',
      label: 'Email',
      prop: 'email',
      width: '280px',
      overflow: 'ellipsis',
      ellipsisOptions: { showTooltip: true },
    },
    { key: 'provider', label: 'Provider', prop: 'provider', width: '100px' },
    { key: 'status', label: 'Status', prop: 'status', width: '110px' },
    {
      key: 'roles',
      label: 'Roles',
      prop: 'roles',
      width: '180px',
      overflow: 'ellipsis',
      ellipsisOptions: { showTooltip: true },
    },
    {
      align: 'right',
      key: 'actions',
      label: 'Actions',
      pinned: 'right',
      width: '190px',
      render: (createElement, _value, record) => {
        const user = (record as UserTableRecord).user;
        const isCurrentUser = user.email.toLocaleLowerCase() === auth.user?.toLocaleLowerCase();
        const isOtpUser = user.provider === 'OTP';
        const canResendLoginLink = user.isActive && isOtpUser;
        const resendLabel = canResendLoginLink
          ? 'Resend login link'
          : isOtpUser
            ? 'Cannot send a login link to an inactive user'
            : 'Login links are only available for OTP users';

        return createElement(
          'vega-flex',
          { gap: 'size-8', justifyContent: 'end' },
          [
            createElement('vega-button-circle', {
              disabled: !canResendLoginLink,
              icon: 'fa-solid fa-paper-plane',
              label: resendLabel,
              onVegaClick: () => openUserDialog('resend', user),
              showTooltip: true,
              size: 'small',
              variant: 'icon-only',
            }),
            createElement('vega-button-circle', {
              icon: 'fa-solid fa-user-pen',
              label: 'Edit roles',
              onVegaClick: () => openUserDialog('roles', user),
              showTooltip: true,
              size: 'small',
              variant: 'icon-only',
            }),
            createElement('vega-button-circle', {
              icon: user.isActive ? 'fa-solid fa-user-slash' : 'fa-solid fa-user-check',
              label: user.isActive ? 'Deactivate' : 'Reactivate',
              onVegaClick: () => openUserDialog('active', user),
              showTooltip: true,
              size: 'small',
              variant: 'icon-only',
            }),
            createElement('vega-button-circle', {
              disabled: isCurrentUser,
              icon: 'fa-solid fa-trash',
              label: isCurrentUser ? 'Cannot delete your own user' : 'Delete user',
              onVegaClick: () => openUserDialog('delete', user),
              showTooltip: true,
              size: 'small',
              variant: 'icon-only',
            }),
          ],
        );
      },
    },
  ];
  const closeDialog = () => {
    inviteMutation.reset();
    resendMutation.reset();
    rolesMutation.reset();
    activeMutation.reset();
    deleteMutation.reset();
    setSelectedUser(undefined);
    setDialog(undefined);
  };
  const normalizedSearchValue = searchValue.trim().toLocaleLowerCase();
  const users = data?.filter(
    (user) =>
      !normalizedSearchValue ||
      user.displayName.toLocaleLowerCase().includes(normalizedSearchValue) ||
      user.email.toLocaleLowerCase().includes(normalizedSearchValue),
  );

  // Roles are not known yet on a hard refresh. Rendering a denial here would flash "Access denied"
  // at an Admin before `/Auth/me` resolves, so the page waits instead of guessing either way.
  if (auth.isLoadingRoles) return <PageSkeleton label="Loading user management" variant="table" />;

  if (!canManageUsers) {
    return (
      <VegaPageNotification
        duration={0}
        message="Your account does not have permission to manage users."
        notificationTitle="Access denied"
        type="error"
      />
    );
  }

  if (isLoading) return <PageSkeleton label="Loading users" variant="results" />;
  if (isError) {
    return (
      <VegaPageNotification
        duration={0}
        message="The user list could not be loaded. Try again."
        notificationTitle="Users unavailable"
        type="error"
      />
    );
  }

  return (
    <VegaFlex direction="col" gap="size-16">
      <VegaFont as="h2" variant="font-h4">
        User management
      </VegaFont>
      {successMessage && (
        <div className={styles.toast}>
          <VegaPageNotification
            duration={3000}
            message={successMessage}
            notificationTitle="User management"
            showCloseButton
            type="success"
          />
        </div>
      )}
      <VegaFlex flexWrap="wrap" gap="size-8">
        <VegaButton label="Invite user" onVegaClick={() => setDialog('invite')} />
      </VegaFlex>
      {data?.length ? (
        <VegaFlex direction="col" gap="size-12">
          <VegaInput
            aria-label="Search users"
            label="Search users"
            onVegaChange={(event) => setSearchValue(event.detail)}
            prefixIcon="fa-solid fa-magnifying-glass"
            showClearIcon
            value={searchValue}
            valueUpdateTrigger="input"
          />
          <VegaFont color="text-secondary" variant="font-field-label">
            {users?.length ?? 0} of {data.length} users
          </VegaFont>
        </VegaFlex>
      ) : null}
      {users?.length ? (
        <VegaCard padding={{ default: 'size-16', M: 'size-24' }} variant="border">
          <VegaTable
            aria-label="Application users"
            columns={columns}
            dataSource={users.map(toTableRecord)}
            density="compact"
            emptyDescription="No users match your search."
            pagination={{
              pageSize: 10,
              pageSizeSelectorOptions: [10, 25, 50],
              position: 'bottom-right',
            }}
            rowDivider
            rowKey="id"
            scrollConfig={{ maxHeight: '520px' }}
            striped
          />
        </VegaCard>
      ) : data?.length ? (
        <VegaPageNotification
          duration={0}
          message="Try a different name or email address."
          notificationTitle="No matching users"
          type="info"
        />
      ) : (
        <VegaPageNotification
          duration={0}
          message="No application users were found."
          notificationTitle="No users"
          type="info"
        />
      )}
      {dialog === 'invite' && (
        <InviteUserDialog
          error={inviteMutation.error}
          isPending={inviteMutation.isPending}
          onClose={closeDialog}
          onSubmit={(request) =>
            inviteMutation.mutate(request, {
              onSuccess: (user) => {
                closeDialog();
                showSuccess(`Invitation and login link sent to ${user.email}.`);
              },
            })
          }
          open
        />
      )}
      {dialog === 'resend' && (
        <ResendLoginLinkDialog
          error={resendMutation.error}
          isPending={resendMutation.isPending}
          onClose={closeDialog}
          onSubmit={() => {
            if (!selectedUser) return;
            resendMutation.mutate(selectedUser.id, {
              onSuccess: () => {
                showSuccess(`Login link sent to ${selectedUser.email}.`);
                closeDialog();
              },
            });
          }}
          open
          user={selectedUser}
        />
      )}
      {dialog === 'roles' && (
        <EditRolesDialog
          error={rolesMutation.error}
          isPending={rolesMutation.isPending}
          onClose={closeDialog}
          onSubmit={(roles) => {
            if (!selectedUser) return;
            rolesMutation.mutate(
              { userId: selectedUser.id, request: { roles } },
              {
                onSuccess: () => {
                  showSuccess(`Roles updated for ${selectedUser.email}.`);
                  closeDialog();
                },
              },
            );
          }}
          open
          user={selectedUser}
        />
      )}
      {dialog === 'active' && (
        <UpdateActiveDialog
          error={activeMutation.error}
          isPending={activeMutation.isPending}
          onClose={closeDialog}
          onSubmit={() => {
            if (!selectedUser) return;
            activeMutation.mutate(
              {
                userId: selectedUser.id,
                request: { isActive: !selectedUser.isActive },
              },
              {
                onSuccess: () => {
                  showSuccess(`${selectedUser.isActive ? 'Deactivated' : 'Reactivated'} ${selectedUser.email}.`);
                  closeDialog();
                },
              },
            );
          }}
          open
          user={selectedUser}
        />
      )}
      {dialog === 'delete' && (
        <DeleteUserDialog
          error={deleteMutation.error}
          isPending={deleteMutation.isPending}
          onClose={closeDialog}
          onSubmit={() => {
            if (!selectedUser) return;
            deleteMutation.mutate(selectedUser.id, {
              onSuccess: () => {
                showSuccess(`Deleted ${selectedUser.email}.`);
                closeDialog();
              },
            });
          }}
          open
          user={selectedUser}
        />
      )}
    </VegaFlex>
  );
};

export default UserManagement;
